import React from "react";
import { graphql } from "react-relay";

import { DebugLogger } from "~/lib/log";
import { OuterObjectContext } from "~/lib/outerObjectContext";
import { smart } from "~/lib/relayUtil";

import AndroidImpl from "./android";
import DesktopImpl from "./desktop";
import IOSImpl from "./ios";
import {
	type NotificationPermissionState as _NotificationPermissionState,
	DeviceContext,
	getDeviceContext,
	NotificationsImpl,
} from "./shared";
import WebImpl from "./web";

import type { notifications_viewer$data as Viewer } from "~/__generated__/notifications_viewer.graphql";

export { DeviceContext, getDeviceContext } from "./shared";
export type NotificationPermissionState = _NotificationPermissionState;

export const NotificationContext = React.createContext<NotificationsImpl | null>(null);
NotificationContext.displayName = "NotificationContext";

const log = new DebugLogger("[Notifications]").log;

interface NotificationProviderProps {
	viewer: Viewer;
	skipRegister?: boolean;
}

interface NotificationProviderState {
	notificationImpl: NotificationsImpl | null;
}

export class DumbNotificationProvider extends React.PureComponent<
	NotificationProviderProps,
	NotificationProviderState
> {
	static contextType = OuterObjectContext;
	declare context: React.ContextType<typeof OuterObjectContext>;

	static implementations = {
		[DeviceContext.WEB]: WebImpl,
		[DeviceContext.ANDROID]: AndroidImpl,
		[DeviceContext.IOS]: IOSImpl,
		[DeviceContext.DESKTOP]: DesktopImpl,
	};

	static Fragments = {
		viewer: graphql`
			fragment notifications_viewer on Viewer {
				id

				user {
					id
					isGuest
				}

				settings {
					pushNotificationsEnabled
				}
			}
		`,
	};

	state: NotificationProviderState = { notificationImpl: null };
	private addedListeners?: boolean;

	public async register(impl: NotificationsImpl) {
		await this.callAddEventListeners(impl);
		await impl.register();
	}

	private async callAddEventListeners(impl: NotificationsImpl): Promise<void> {
		if (this.addedListeners) return;
		this.addedListeners = true;
		await impl.addEventListeners();
	}

	async componentDidMount() {
		const ctx = getDeviceContext();

		if (!ctx) {
			console.warn("Notifications not supported");
			return;
		}

		const impl = new DumbNotificationProvider.implementations[ctx](this);
		log("Initialized", { ctx });

		const { user, settings } = this.props.viewer;

		// If the user has enabled push notifications, try to register everything.
		// Should the user revoke notification permissions, this shouldn't do
		// anything, and the user will have to go to the settings page to try to
		// get permissions again.
		// Skip this if the user was dropped in from the onboarding flow
		if (!user.isGuest && settings?.pushNotificationsEnabled && !this.props.skipRegister) {
			log("User has enabled notifications, registering");
			await this.register(impl);
		}

		this.setState({ notificationImpl: impl });
	}

	async componentDidUpdate(prevProps: NotificationProviderProps) {
		const { user, settings } = this.props.viewer;

		// Notifications were disabled before but are now enabled
		if (
			!prevProps.viewer.settings?.pushNotificationsEnabled &&
			settings?.pushNotificationsEnabled &&
			this.state.notificationImpl &&
			!user.isGuest
		) {
			log("Notifications enabled");
			await this.register(this.state.notificationImpl);
		}
	}

	render() {
		return (
			<NotificationContext.Provider value={this.state.notificationImpl}>
				{this.props.children}
			</NotificationContext.Provider>
		);
	}
}
export default smart(DumbNotificationProvider);
